import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import { initializePushNotifications, areNotificationsEnabled } from '../../utils/pushNotifications';
import Layout from '../../components/Layout';

const AdminNotifications = ({ onNavigate, userInfo = {} }) => {
    // Mobile state (used for responsive content layout)
    const [isMobile, setIsMobile] = useState(false);

    // Notification form state
    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationMessage, setNotificationMessage] = useState('');
    const [notificationIcon, setNotificationIcon] = useState('/logo192.png');

    // UI state
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // History and stats
    const [notificationHistory, setNotificationHistory] = useState([]);
    const [stats, setStats] = useState({
        totalNotifications: 0,
        totalSubscribers: 0,
        notificationsToday: 0,
        successRate: 0
    });
    const [historyPage, setHistoryPage] = useState(1);
    const [totalHistoryPages, setTotalHistoryPages] = useState(1);

    // Notification permission status
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);

    // Check mobile on mount
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Check notification permission status
    useEffect(() => {
        setNotificationsEnabled(areNotificationsEnabled());
    }, []);

    // Fetch notification history and stats
    useEffect(() => {
        fetchNotificationHistory();
        fetchNotificationStats();
    }, [historyPage]);

    const fetchNotificationHistory = async () => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(
                `${API_LINK}/api/admin/notifications/history?page=${historyPage}&limit=10`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const result = await response.json();
                setNotificationHistory(result.data.notifications);
                setTotalHistoryPages(result.data.pagination.totalPages);
            }
        } catch (error) {
            console.error('Error fetching notification history:', error);
        }
    };

    const fetchNotificationStats = async () => {
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(
                `${API_LINK}/api/admin/notifications/stats`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (error) {
            console.error('Error fetching notification stats:', error);
        }
    };

    const handleEnableNotifications = async () => {
        try {
            setLoading(true);
            const result = await initializePushNotifications();

            if (result.success) {
                setNotificationsEnabled(true);
                setSuccess('Notifications enabled successfully!');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(result.message);
                setTimeout(() => setError(null), 5000);
            }
        } catch (error) {
            setError('Failed to enable notifications');
            setTimeout(() => setError(null), 5000);
        } finally {
            setLoading(false);
        }
    };

    const handleSendNotification = async (e) => {
        e.preventDefault();

        if (!notificationTitle || !notificationMessage) {
            setError('Title and message are required');
            setTimeout(() => setError(null), 3000);
            return;
        }

        try {
            setSending(true);
            setError(null);
            setSuccess(null);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(
                `${API_LINK}/api/admin/notifications/send`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        title: notificationTitle,
                        message: notificationMessage,
                        icon: notificationIcon
                    })
                }
            );

            const result = await response.json();

            if (response.ok && result.success) {
                setSuccess(`Notification sent successfully to ${result.data.successfulSends} users!`);
                setNotificationTitle('');
                setNotificationMessage('');
                setNotificationIcon('/logo192.png');

                // Refresh history and stats
                fetchNotificationHistory();
                fetchNotificationStats();

                setTimeout(() => setSuccess(null), 5000);
            } else {
                setError(result.message || 'Failed to send notification');
                setTimeout(() => setError(null), 5000);
            }
        } catch (error) {
            console.error('Send notification error:', error);
            setError('Failed to send notification. Please try again.');
            setTimeout(() => setError(null), 5000);
        } finally {
            setSending(false);
        }
    };

    const handleSendTestNotification = async () => {
        if (!notificationTitle || !notificationMessage) {
            setError('Title and message are required');
            setTimeout(() => setError(null), 3000);
            return;
        }

        try {
            setSending(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(
                `${API_LINK}/api/admin/notifications/send-test`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        title: notificationTitle,
                        message: notificationMessage
                    })
                }
            );

            const result = await response.json();

            if (response.ok && result.success) {
                setSuccess('Test notification sent to you!');
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(result.message || 'Failed to send test notification');
                setTimeout(() => setError(null), 5000);
            }
        } catch (error) {
            console.error('Send test notification error:', error);
            setError('Failed to send test notification');
            setTimeout(() => setError(null), 5000);
        } finally {
            setSending(false);
        }
    };



    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Layout role="admin" onNavigate={onNavigate} activePage="admin-notifications" userInfo={userInfo}>
            {/* Page Header */}
            <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                    📢 Push Notification Management
                </h2>
                <p style={{ margin: 0, opacity: 0.9 }}>
                    Send instant notifications to all users' devices
                </p>
            </div>

            {/* Alert Messages */}
            {error && (
                <div style={{
                    backgroundColor: '#fee',
                    color: '#c33',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #fcc'
                }}>
                    ⚠️ {error}
                </div>
            )}

            {success && (
                <div style={{
                    backgroundColor: '#efe',
                    color: '#3c3',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #cfc'
                }}>
                    ✅ {success}
                </div>
            )}

            {/* Notification Permission Status */}
            {!notificationsEnabled && (
                <div style={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '2rem',
                    border: '1px solid #ffeaa7',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '1rem'
                }}>
                    <div>
                        <strong>🔔 Enable Notifications</strong>
                        <p style={{ margin: '0.5rem 0 0 0' }}>
                            You need to enable notifications to receive test notifications
                        </p>
                    </div>
                    <button
                        onClick={handleEnableNotifications}
                        disabled={loading}
                        style={{
                            backgroundColor: '#238548',
                            color: 'white',
                            border: 'none',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '8px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: '500',
                            opacity: loading ? 0.6 : 1
                        }}
                    >
                        {loading ? 'Enabling...' : 'Enable Notifications'}
                    </button>
                </div>
            )}

            {/* Statistics Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {[
                    { icon: '📊', label: 'Total Sent', value: stats.totalNotifications, color: '#3b82f6' },
                    { icon: '👥', label: 'Subscribers', value: stats.totalSubscribers, color: '#10b981' },
                    { icon: '📅', label: 'Today', value: stats.notificationsToday, color: '#f59e0b' },
                    { icon: '✅', label: 'Success Rate', value: `${stats.successRate}%`, color: '#8b5cf6' }
                ].map((stat, index) => (
                    <div key={index} style={{
                        background: '#ffffff',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{stat.icon}</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: stat.color, margin: '0.5rem 0' }}>
                            {stat.value}
                        </div>
                        <div style={{ color: '#666', fontSize: '0.9rem' }}>{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Send Notification Form */}
            <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem'
            }}>
                <h3 style={{ margin: '0 0 1.5rem 0', color: '#333' }}>
                    📝 Compose Notification
                </h3>

                <form onSubmit={handleSendNotification}>
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '500',
                            color: '#333'
                        }}>
                            Title *
                        </label>
                        <input
                            type="text"
                            value={notificationTitle}
                            onChange={(e) => setNotificationTitle(e.target.value)}
                            placeholder="Enter notification title"
                            maxLength={50}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                borderRadius: '8px',
                                border: '1px solid #ddd',
                                fontSize: '1rem',
                                boxSizing: 'border-box'
                            }}
                            required
                        />
                        <small style={{ color: '#666' }}>
                            {notificationTitle.length}/50 characters
                        </small>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '500',
                            color: '#333'
                        }}>
                            Message *
                        </label>
                        <textarea
                            value={notificationMessage}
                            onChange={(e) => setNotificationMessage(e.target.value)}
                            placeholder="Enter notification message"
                            maxLength={200}
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                borderRadius: '8px',
                                border: '1px solid #ddd',
                                fontSize: '1rem',
                                boxSizing: 'border-box',
                                resize: 'vertical'
                            }}
                            required
                        />
                        <small style={{ color: '#666' }}>
                            {notificationMessage.length}/200 characters
                        </small>
                    </div>

                    {/* Preview */}
                    {(notificationTitle || notificationMessage) && (
                        <div style={{
                            background: '#f8f9fa',
                            padding: '1.5rem',
                            borderRadius: '8px',
                            marginBottom: '1.5rem',
                            border: '1px solid #e9ecef'
                        }}>
                            <h4 style={{ margin: '0 0 1rem 0', color: '#666', fontSize: '0.9rem' }}>
                                📱 Preview
                            </h4>
                            <div style={{
                                background: '#ffffff',
                                padding: '1rem',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                display: 'flex',
                                gap: '1rem',
                                alignItems: 'flex-start'
                            }}>
                                <img
                                    src={notificationIcon}
                                    alt="Icon"
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '8px'
                                    }}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                                        {notificationTitle || 'Notification Title'}
                                    </div>
                                    <div style={{ color: '#666', fontSize: '0.9rem' }}>
                                        {notificationMessage || 'Notification message will appear here'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap'
                    }}>
                        <button
                            type="submit"
                            disabled={sending || !notificationTitle || !notificationMessage}
                            style={{
                                flex: 1,
                                minWidth: '200px',
                                backgroundColor: '#238548',
                                color: 'white',
                                border: 'none',
                                padding: '1rem 2rem',
                                borderRadius: '8px',
                                cursor: (sending || !notificationTitle || !notificationMessage) ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: '500',
                                opacity: (sending || !notificationTitle || !notificationMessage) ? 0.6 : 1,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {sending ? '📤 Sending...' : '📢 Send to All Users'}
                        </button>

                        <button
                            type="button"
                            onClick={handleSendTestNotification}
                            disabled={sending || !notificationTitle || !notificationMessage || !notificationsEnabled}
                            style={{
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                padding: '1rem 2rem',
                                borderRadius: '8px',
                                cursor: (sending || !notificationTitle || !notificationMessage || !notificationsEnabled) ? 'not-allowed' : 'pointer',
                                fontSize: '1rem',
                                fontWeight: '500',
                                opacity: (sending || !notificationTitle || !notificationMessage || !notificationsEnabled) ? 0.6 : 1
                            }}
                        >
                            🧪 Send Test
                        </button>
                    </div>
                </form>
            </div>

            {/* Notification History */}
            <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <h3 style={{ margin: '0 0 1.5rem 0', color: '#333' }}>
                    📜 Notification History
                </h3>

                {notificationHistory.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '3rem',
                        color: '#666'
                    }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                        <p>No notifications sent yet</p>
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse'
                            }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Title</th>
                                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Message</th>
                                        <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Recipients</th>
                                        <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Success</th>
                                        <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Sent At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notificationHistory.map((notification) => (
                                        <tr key={notification.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                                            <td style={{ padding: '1rem' }}>
                                                <strong>{notification.title}</strong>
                                            </td>
                                            <td style={{ padding: '1rem', maxWidth: '300px' }}>
                                                <div style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {notification.message}
                                                </div>
                                            </td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                {notification.total_recipients}
                                            </td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '12px',
                                                    backgroundColor: notification.successful_sends === notification.total_recipients ? '#d4edda' : '#fff3cd',
                                                    color: notification.successful_sends === notification.total_recipients ? '#155724' : '#856404',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '500'
                                                }}>
                                                    {notification.successful_sends}/{notification.total_recipients}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', fontSize: '0.9rem', color: '#666' }}>
                                                {formatDate(notification.sent_at)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalHistoryPages > 1 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '1rem',
                                marginTop: '1.5rem'
                            }}>
                                <button
                                    onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                                    disabled={historyPage === 1}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        backgroundColor: historyPage === 1 ? '#f8f9fa' : '#ffffff',
                                        cursor: historyPage === 1 ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    ← Previous
                                </button>
                                <span style={{ color: '#666' }}>
                                    Page {historyPage} of {totalHistoryPages}
                                </span>
                                <button
                                    onClick={() => setHistoryPage(Math.min(totalHistoryPages, historyPage + 1))}
                                    disabled={historyPage === totalHistoryPages}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '8px',
                                        border: '1px solid #ddd',
                                        backgroundColor: historyPage === totalHistoryPages ? '#f8f9fa' : '#ffffff',
                                        cursor: historyPage === totalHistoryPages ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
};

export default AdminNotifications;
